import React from 'react'
import { 
  FiX, 
  FiUser, 
  FiMail, 
  FiKey, 
  FiShield, 
  FiCheck,
  FiSave,
  FiUserPlus,
  FiLock,
  FiAward,
  FiSettings,
  FiEye,
  FiEyeOff
} from "react-icons/fi";
import { MdOutlineAdminPanelSettings, MdOutlineClose } from "react-icons/md";
import Select from 'react-select';

const CreateModal = ({ 
  onCancel, 
  onSubmit, 
  title, 
  inputData, 
  handleONChange, 
  multiSelectOptions, 
  handleMultiSelectChange, 
  error, 
  roleType, 
  handleRoleSelect, 
  selectedRoleType 
}) => {
  const isFormValid = inputData?.name && inputData?.email && selectedRoleType && inputData?.permissions?.length > 0 && inputData?.password;

  // Custom styles for React Select
  const customSelectStyles = {
    control: (provided, state) => ({
      ...provided,
      borderColor: state.isFocused ? '#715EFE' : '#D1D5DB',
      boxShadow: state.isFocused ? '0 0 0 3px rgba(113, 94, 254, 0.2)' : 'none',
      borderRadius: '0.5rem',
      padding: '0.125rem 0',
      minHeight: '42px',
      '&:hover': {
        borderColor: '#715EFE',
      },
    }),
    option: (provided, state) => ({
      ...provided,
      backgroundColor: state.isSelected ? '#715EFE' : state.isFocused ? '#F3F0FF' : 'white',
      color: state.isSelected ? 'white' : '#1F2937',
      '&:hover': {
        backgroundColor: state.isSelected ? '#715EFE' : '#F3F0FF',
      },
    }),
    multiValue: (provided) => ({
      ...provided,
      backgroundColor: '#F3F0FF',
      borderRadius: '0.375rem',
    }),
    multiValueLabel: (provided) => ({
      ...provided,
      color: '#715EFE',
      fontWeight: 500,
      fontSize: '0.813rem',
    }),
    multiValueRemove: (provided) => ({
      ...provided,
      color: '#715EFE',
      '&:hover': {
        backgroundColor: '#715EFE',
        color: 'white',
        borderRadius: '0 0.375rem 0.375rem 0',
      },
    }),
    placeholder: (provided) => ({
      ...provided,
      color: '#9CA3AF',
      fontSize: '0.875rem',
    }),
    menu: (provided) => ({
      ...provided,
      borderRadius: '0.5rem',
      boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
      zIndex: 50,
    }),
    singleValue: (provided) => ({
      ...provided,
      color: '#1F2937',
      fontSize: '0.875rem',
    }),
    input: (provided) => ({
      ...provided,
      padding: '0',
      margin: '0',
    }),
    valueContainer: (provided) => ({
      ...provided,
      padding: '0.125rem 0.75rem',
    }),
    indicatorsContainer: (provided) => ({
      ...provided,
      padding: '0 0.75rem',
    }),
  };

  // Get role icon
  const getRoleIcon = (role) => {
    if (role?.value === 'super_admin') return <FiAward className="w-4 h-4" />;
    if (role?.value === 'sub_admin') return <FiShield className="w-4 h-4" />;
    return <FiUser className="w-4 h-4" />;
  };

  // Custom option renderer
  const formatOptionLabel = (option) => (
    <div className="flex items-center gap-2">
      {getRoleIcon(option)}
      <span>{option.label}</span>
    </div>
  );

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm animate-in fade-in duration-200">
      <div className="w-full max-w-2xl bg-white rounded-2xl shadow-2xl animate-in zoom-in-95 duration-200 max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 flex-shrink-0">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-gradient-to-br from-[#855DF6]/10 to-[#715EFE]/10 rounded-xl">
              <MdOutlineAdminPanelSettings className="w-5 h-5 text-[#715EFE]" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-800">{title}</h3>
              <p className="text-xs text-gray-400 hidden sm:block">Fill in the details to create a new admin</p>
            </div>
          </div>
          <button
            className="flex items-center justify-center w-9 h-9 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-all duration-200"
            onClick={onCancel}
          >
            <FiX className="w-5 h-5" />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-6 space-y-4">
          {/* Name & Email Grid */}
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div>
              <label className="flex items-center gap-2 mb-1 text-sm font-medium text-gray-700">
                <FiUser className="w-4 h-4 text-gray-400" />
                <span>Full Name</span>
                <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <input
                  type="text"
                  name="name"
                  value={inputData?.name || ''}
                  onChange={handleONChange}
                  placeholder="Enter full name"
                  className="w-full pl-10 pr-3 py-2.5 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#715EFE]/20 focus:border-[#715EFE] transition-all duration-200"
                />
                <FiUser className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              </div>
              {error?.name && <p className="mt-1 text-sm text-red-500">{error?.name}</p>}
            </div>

            <div>
              <label className="flex items-center gap-2 mb-1 text-sm font-medium text-gray-700">
                <FiMail className="w-4 h-4 text-gray-400" />
                <span>Email Address</span>
                <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <input
                  type="email"
                  name="email"
                  value={inputData?.email || ''}
                  onChange={handleONChange}
                  placeholder="Enter email address"
                  className="w-full pl-10 pr-3 py-2.5 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#715EFE]/20 focus:border-[#715EFE] transition-all duration-200"
                />
                <FiMail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              </div>
              {error?.email && <p className="mt-1 text-sm text-red-500">{error?.email}</p>}
            </div>
          </div>

          {/* Role Selection */}
          <div>
            <label className="flex items-center gap-2 mb-1 text-sm font-medium text-gray-700">
              <FiShield className="w-4 h-4 text-gray-400" />
              <span>Role</span>
              <span className="text-red-500">*</span>
            </label>
            <Select
              options={roleType}
              value={selectedRoleType}
              onChange={handleRoleSelect}
              className="basic-multi-select"
              classNamePrefix="select"
              styles={customSelectStyles}
              formatOptionLabel={formatOptionLabel}
              placeholder="Select a role..."
              isSearchable={false}
            />
            {error?.roleType && <p className="mt-1 text-sm text-red-500">{error?.roleType}</p>}
          </div>

          {/* Permissions Selection */}
          <div>
            <label className="flex items-center gap-2 mb-1 text-sm font-medium text-gray-700">
              <FiSettings className="w-4 h-4 text-gray-400" />
              <span>Access Permissions</span>
              <span className="text-red-500">*</span>
            </label>
            <Select
              isMulti
              options={multiSelectOptions}
              value={inputData?.permissions || []}
              onChange={handleMultiSelectChange}
              className="basic-multi-select"
              classNamePrefix="select"
              styles={customSelectStyles}
              placeholder="Select permissions..."
            />
            {error?.permissions && <p className="mt-1 text-sm text-red-500">{error?.permissions}</p>}
          </div>

          {/* Password */}
          <div>
            <label className="flex items-center gap-2 mb-1 text-sm font-medium text-gray-700">
              <FiKey className="w-4 h-4 text-gray-400" />
              <span>Password</span>
              <span className="text-red-500">*</span>
            </label>
            <div className="relative">
              <input
                type="password"
                name="password"
                value={inputData?.password || ''}
                onChange={handleONChange}
                placeholder="Enter password"
                className="w-full pl-10 pr-3 py-2.5 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#715EFE]/20 focus:border-[#715EFE] transition-all duration-200"
              />
              <FiLock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            </div>
            {error?.password && <p className="mt-1 text-sm text-red-500">{error?.password}</p>}
          </div>

          {/* Form validity indicator */}
          {!isFormValid && (
            <div className="flex items-center gap-2 p-3 text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-lg">
              <FiX className="w-4 h-4 text-amber-500 flex-shrink-0" />
              <span>Please fill in all required fields to create an admin</span>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex flex-col-reverse sm:flex-row items-center justify-end gap-3 px-6 py-4 border-t border-gray-100 flex-shrink-0 bg-gray-50/50 rounded-b-2xl">
          <button
            className="w-full sm:w-auto px-6 py-2.5 text-sm font-medium text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg transition-all duration-200"
            onClick={onCancel}
          >
            Cancel
          </button>
          <button
            className={`w-full sm:w-auto flex items-center justify-center gap-2 px-6 py-2.5 text-sm font-medium text-white rounded-lg transition-all duration-200 ${
              isFormValid 
                ? 'bg-gradient-to-r from-[#855DF6] to-[#715EFE] hover:shadow-lg hover:shadow-[#715EFE]/25 hover:-translate-y-0.5' 
                : 'bg-gray-300 cursor-not-allowed'
            }`}
            onClick={onSubmit}
            disabled={!isFormValid}
          >
            <FiUserPlus className="w-4 h-4" />
            <span>Create Admin</span>
          </button>
        </div>
      </div>
    </div>
  )
}

export default CreateModal